import React, { useCallback, useEffect, useState } from "react"
import { useDropzone } from "react-dropzone"
import { upload } from "lib/firebase"
import readFile from "utils/readFile"
import Progress from "components/Base/Progress"

const InputTags = ({ value, onChange }) => {
  const [input, setInput] = useState("")
  const [tags, setTags] = useState([])

  const _onChange = (e) => {
    const { value } = e.target
    setInput(value)
  }

  const _onKeyDown = (e) => {
    const { key } = e
    // e.stopPropagation()
    // e.preventDefault()
    if (e.key === "Enter" || e.keyCode === 13) {
      // Do something
      e.preventDefault()
    }

    const trimmedInput = input?.trim()?.toLowerCase()
    if (key === "," && trimmedInput.length && !tags.includes(trimmedInput)) {
      e.preventDefault()
      setTags((prevState) => [...prevState, trimmedInput])
      setInput("")
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const raw = e.clipboardData.getData("text/plain")
    const pastedTags = raw.split(",")
    setTags((prevState) => [...prevState, ...pastedTags])
    setInput("")
  }

  const deleteTag = (index) => {
    setTags((prevState) => prevState.filter((tag, i) => i !== index))
  }

  //TODO: HERE
  useEffect(() => {
    if (tags.length > 0) {
      onChange(tags)
    }
  }, [tags])

  return (
    <div className="flex space-x-4 relative z-50">
      <input
        className="lowercase"
        type="text"
        value={input}
        placeholder="Enter a tag and press ,"
        onKeyDown={_onKeyDown}
        onChange={_onChange}
        onPaste={handlePaste}
      />

      <ul className="flex space-x-2 text-xs">
        {tags.map((tag, index) => (
          <li
            key={index}
            className="flex items-center space-x-2 bg-gray-100 text-gray-800 p-2 rounded-lg"
          >
            <span>{tag}</span>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                deleteTag(index)
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

const InputDropzone = ({ tags, onChange }) => {
  const [files, setFiles] = useState(null)

  const onDrop = async (acceptedFiles) => {
    try {
      const files = await Promise.all([...acceptedFiles.map(readFile)])

      if (files.length === 0) return

      setFiles((prevState) => [...(prevState ? prevState : []), ...files])

      const uploadedFiles = await Promise.all([
        ...files.map((file, index) =>
          upload(file, (progress) =>
            setFiles((prevState) =>
              prevState.map((x) => ({
                ...x,
                progress: x.id === file.id ? progress : x.progress,
              })),
            ),
          ),
        ),
      ])

      setFiles((prevState) => [
        ...(prevState
          ? prevState.map((item) => {
              const existing = uploadedFiles.find(
                (_) => _.name === item.metadata.name,
              )
              return {
                ...item,
                metadata: {
                  ...item.metadata,
                  ...(existing ? existing : {}),
                },
              }
            })
          : []),
      ])
    } catch (error) {
      console.error(error)
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
  })

  const handleFileRemove = (id) => {
    if (!id) return
    setFiles(files.filter((file) => file.id !== id))
  }

  const handleTagsChange = useCallback(
    (file) => {
      setFiles((prevState) =>
        files.map((item) => ({
          ...item,
          metadata: {
            ...item.metadata,
            tags:
              item.metadata.name === file.metadata.name ? file.tags : item.tags,
          },
        })),
      )
    },
    [setFiles, files],
  )

  useEffect(() => {
    if (files) {
      onChange(files.map((_) => _.metadata))
    }
  }, [files])

  return (
    <div className="flex flex-col w-full h-full">
      <div {...getRootProps({ onClick: (e) => e.stopPropagation() })}>
        <input {...getInputProps()} />

        <div className="w-full h-48 flex flex-col justify-center items-center cursor-pointer bg-gray-100 hover:bg-gray-200">
          <svg
            width="48"
            height="48"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              fill="#4A5568"
            />
          </svg>

          <div className="text-gray-600 text-sm  mt-2">
            <p>Drag and drop your file or click to browse</p>
          </div>
        </div>
      </div>

      <ul className="mt-6 space-y-4">
        {files?.map((file, index) => (
          <li key={index}>
            <FileUploadPreview {...file} onDelete={handleFileRemove}>
              <div className="w-full">
                {tags && (
                  <InputTags
                    value={file}
                    onChange={(tags) => {
                      setFiles((prevState) =>
                        files.map((item, idx) => ({
                          ...item,
                          metadata: {
                            ...item.metadata,
                            ...(index === idx ? { tags } : {}),
                          },
                        })),
                      )
                    }}
                  />
                )}
              </div>
            </FileUploadPreview>
          </li>
        ))}
      </ul>
    </div>
  )
}

InputDropzone.defaultProps = {
  tags: true,
}

export default InputDropzone

const FileUploadPreview = ({
  id,
  preview,
  metadata,
  progress,
  onDelete,
  children,
  ...rest
}) => {
  const isImage = metadata?.type?.includes("image")

  return (
    <div className="flex flex-wrap border-2 border-gray-100 rounded-lg">
      <div className="w-4/12">
        {isImage ? (
          <div className="aspect-w-16 aspect-h-9">
            <img
              className="w-full h-full object-cover rounded-lg"
              src={preview}
            />
          </div>
        ) : (
          <div className="aspect-w-16 aspect-h-9 flex justify-center items-center ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-24 w-24"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="flex-1 p-4 flex flex-col space-y-2">
        <p>{metadata.name}</p>
        <Progress value={progress} />
        {children}
      </div>

      {/* <button type="button" onClick={() => onDelete(id)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button> */}
    </div>
  )
}
